/**
 * Figures behind the trend charts, worked out from the classified ledger so
 * they count exactly what the spending figures count: rows in the twelve-month
 * window treated as spending (one-offs, transfers and investment activity are
 * left out). Pure functions, so the charts' numbers are pinned by tests.
 */
import type { Amount, Holding, LedgerRow, MonthFlow } from './analysis'
import { isInferredRow } from './details'
import type { Row } from './google/sheets'

const round = (n: number) => Math.round(n * 100) / 100
const str = (v: unknown) => String(v ?? '').trim()

/** The rows the spending figures count. A refund is negative spending. */
export const spendingRows = (ledger: LedgerRow[]) => ledger.filter((r) => r.in_window && r.treatment === 'spending')

function median(values: number[]): number {
  if (values.length === 0) return 0
  const s = [...values].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

/** The time ranges the charts can show: the last few months to the latest statement, or everything. */
export const RANGES = [
  { id: '3m', label: '3M', months: 3 },
  { id: '6m', label: '6M', months: 6 },
  { id: '1y', label: '1Y', months: 12 },
  { id: '2y', label: '2Y', months: 24 },
  { id: 'all', label: 'All', months: null },
] as const
export type RangeId = (typeof RANGES)[number]['id']

/** Dates after `from` (null: from the first statement) up to and including `to`. */
export interface Span {
  from: string | null
  to: string
}

/** The last `months` months to `asOf`, to the day; the 1Y span is the spending window. */
export function spanOf(asOf: string, months: number | null): Span {
  if (months === null) return { from: null, to: asOf }
  const [y, m, d] = asOf.split('-').map(Number)
  const n = y * 12 + (m - 1) - months
  // The 31st, months back, is the last day of a shorter month.
  const day = Math.min(d, new Date(Date.UTC(Math.floor(n / 12), (n % 12) + 1, 0)).getUTCDate())
  return { from: `${Math.floor(n / 12)}-${String((n % 12) + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`, to: asOf }
}

/** The first day a span shows: the day after `from`. */
export function firstDay(span: Span, earliest: string | null): string | null {
  if (span.from === null) return earliest
  const d = new Date(`${span.from}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

/** A checking, savings or card row dated inside the span. */
export const inSpan = (r: LedgerRow, span: Span) => r.cash && (span.from === null || r.date > span.from) && r.date <= span.to

/** The ledger with `in_window` moved to the span, so every figure below counts that span. */
export const withinSpan = (ledger: LedgerRow[], span: Span): LedgerRow[] => ledger.map((r) => ({ ...r, in_window: inSpan(r, span) }))

/**
 * The ranges worth offering: each one past the first only if the statements
 * reach back before the range below it starts, so it shows something more.
 */
export function offeredRanges(earliest: string | null, asOf: string): Set<RangeId> {
  const offered = new Set<RangeId>([RANGES[0].id])
  for (let i = 1; i < RANGES.length; i++) {
    const below = spanOf(asOf, RANGES[i - 1].months).from
    if (earliest && below && earliest <= below) offered.add(RANGES[i].id)
  }
  return offered
}

/**
 * A span of N months to the day touches N + 1 calendar months, with a
 * part-month at each end. Month-by-month charts drop whichever end covers less
 * than half its month, so a few days don't read as a quiet month. Everything
 * (`n` null) keeps every month.
 */
export function fullMonths(months: string[], asOf: string | null, n: number | null = 12): string[] {
  if (!asOf || n === null || months.length < n + 1) return months
  return Number(asOf.slice(8, 10)) >= 15 ? months.slice(1) : months.slice(0, -1)
}

/** Income and spending per month, as the cash-flow figures count them. */
export function monthFlows(ledger: LedgerRow[]): MonthFlow[] {
  const flows = new Map<string, MonthFlow>()
  for (const r of ledger) {
    if (!r.in_window || (r.treatment !== 'income' && r.treatment !== 'spending')) continue
    const f = flows.get(r.month) ?? { month: r.month, income: 0, spending: 0, net: 0 }
    if (r.treatment === 'income') f.income += r.amount
    else f.spending -= r.amount
    flows.set(r.month, f)
  }
  return [...flows.values()]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((f) => ({ month: f.month, income: round(f.income), spending: round(f.spending), net: round(f.income - f.spending) }))
}

type Purchase = { date: string; merchant: string; amount: number; category: string; account_id: string }

/** The spending tab's totals and lists over the rows in the window, as the analysis works them out for the year. */
export function spendingSummary(ledger: LedgerRow[]) {
  const byCategory = new Map<string, Amount>()
  const byMerchant = new Map<string, Amount>()
  const add = (map: Map<string, Amount>, label: string, amount: number) => {
    const a = map.get(label) ?? { label, total: 0, count: 0 }
    a.total += amount
    a.count += 1
    map.set(label, a)
  }
  const sorted = (map: Map<string, Amount>) => [...map.values()].map((a) => ({ ...a, total: round(a.total) })).sort((a, b) => b.total - a.total)
  const largest: Purchase[] = []
  const oneOffs: Purchase[] = []
  let total = 0
  let first: string | null = null
  for (const r of ledger) {
    if (!r.in_window || (r.treatment !== 'spending' && r.treatment !== 'one_off')) continue
    const p = { date: r.date, merchant: r.merchant, amount: round(-r.amount), category: r.category, account_id: r.account_id }
    if (r.treatment === 'one_off') {
      oneOffs.push(p)
      continue
    }
    if (!first || r.date < first) first = r.date
    total -= r.amount
    add(byCategory, r.group, -r.amount)
    add(byMerchant, r.merchant, -r.amount)
    // A refund nets off the totals but isn't a purchase.
    if (r.amount < 0) largest.push(p)
  }
  return {
    from: first,
    total: round(total),
    by_category: sorted(byCategory),
    top_merchants: sorted(byMerchant).slice(0, 10),
    largest: largest.sort((a, b) => b.amount - a.amount).slice(0, 10),
    one_offs: {
      total: round(oneOffs.reduce((s, p) => s + p.amount, 0)),
      count: oneOffs.length,
      items: oneOffs.sort((a, b) => b.amount - a.amount).slice(0, 10),
    },
  }
}

export interface SavingsRate {
  month: string
  /** (income − spending) ÷ income; null for a month with no income. */
  rate: number | null
}

/** Each month's savings rate, and the rate over all of them together. */
export function savingsRates(months: MonthFlow[]): { months: SavingsRate[]; overall: number | null } {
  const income = months.reduce((s, m) => s + m.income, 0)
  const spending = months.reduce((s, m) => s + m.spending, 0)
  return {
    months: months.map((m) => ({ month: m.month, rate: m.income > 0 ? (m.income - m.spending) / m.income : null })),
    overall: income > 0 ? (income - spending) / income : null,
  }
}

export interface CategorySeries {
  /** The parent category ("Cars"), as the charts group spending. */
  category: string
  /** Spending in each of `months`, in order. */
  values: number[]
  total: number
}

/** Monthly spending for the `top` parent categories by total, over the given months. */
export function categoryTrends(ledger: LedgerRow[], months: string[], top = 6): CategorySeries[] {
  const index = new Map(months.map((m, i) => [m, i]))
  const by = new Map<string, number[]>()
  for (const r of spendingRows(ledger)) {
    const i = index.get(r.month)
    if (i === undefined) continue
    const values = by.get(r.group) ?? months.map(() => 0)
    values[i] += -r.amount
    by.set(r.group, values)
  }
  return [...by]
    .map(([category, values]) => ({ category, values: values.map(round), total: round(values.reduce((s, v) => s + v, 0)) }))
    .filter((c) => c.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, top)
}

/**
 * The month to compare against the usual: the month of `asOf` once it is
 * nearly over (day 28 or later), otherwise the month before it, so a
 * half-finished month is not read as a quiet one.
 */
export function comparisonMonth(asOf: string): string {
  const [y, m, d] = asOf.split('-').map(Number)
  if (d >= 28) return asOf.slice(0, 7)
  const prev = new Date(Date.UTC(y, m - 2, 1))
  return prev.toISOString().slice(0, 7)
}

export interface VersusUsual {
  category: string
  /** Spending in the comparison month. */
  latest: number
  /** Median, lowest and highest month among the other months of the window. */
  usual: number
  low: number
  high: number
}

/**
 * Each of the top categories in one month against its other months. Months
 * with no spending in a category count as zero, so a category you rarely use
 * shows a usual of nothing.
 */
export function versusUsual(ledger: LedgerRow[], months: string[], month: string, top = 8): VersusUsual[] {
  if (!months.includes(month)) return []
  const others = months.filter((m) => m !== month)
  if (others.length === 0) return []
  return categoryTrends(ledger, months, top).map((c) => {
    const past = c.values.filter((_, i) => months[i] !== month)
    return {
      category: c.category,
      latest: c.values[months.indexOf(month)],
      usual: round(median(past)),
      low: Math.min(...past),
      high: Math.max(...past),
    }
  })
}

export interface RecurringMonth {
  month: string
  total: number
  charges: { merchant: string; amount: number }[]
  /** Recurring merchants first charged this month, after the window's first month. */
  started: string[]
}

/** What the recurring charges came to each month, and when each one began. */
export function recurringByMonth(ledger: LedgerRow[], merchants: string[], months: string[]): RecurringMonth[] {
  const wanted = new Set(merchants)
  const by = new Map(months.map((m) => [m, new Map<string, number>()]))
  const first = new Map<string, string>()
  for (const r of spendingRows(ledger)) {
    if (!wanted.has(r.merchant)) continue
    const month = by.get(r.month)
    if (!month) continue
    month.set(r.merchant, (month.get(r.merchant) ?? 0) - r.amount)
    if (r.month < (first.get(r.merchant) ?? '9999')) first.set(r.merchant, r.month)
  }
  return months.map((m, i) => {
    const charges = [...by.get(m)!].map(([merchant, amount]) => ({ merchant, amount: round(amount) })).sort((a, b) => b.amount - a.amount)
    return {
      month: m,
      total: round(charges.reduce((s, c) => s + c.amount, 0)),
      charges,
      started: i === 0 ? [] : [...first].filter(([, f]) => f === m).map(([merchant]) => merchant).sort(),
    }
  })
}

/** Spending per calendar day, `YYYY-MM-DD` → amount. */
export function dailySpending(ledger: LedgerRow[]): Map<string, number> {
  const days = new Map<string, number>()
  for (const r of spendingRows(ledger)) days.set(r.date, round((days.get(r.date) ?? 0) - r.amount))
  return days
}

/** What each fund costs a year: value × expense ratio, for holdings whose statements print the ratio. */
export function annualFees(holdings: Holding[]): { label: string; total: number; ratio: number; value: number }[] {
  return holdings
    .filter((h) => h.expense_ratio !== null && h.expense_ratio > 0 && h.value > 0)
    .map((h) => ({ label: h.symbol || h.name, total: round((h.value * h.expense_ratio!) / 100), ratio: h.expense_ratio!, value: h.value }))
    .sort((a, b) => b.total - a.total)
}

/** The emergency-fund target in months from the Profile tab: the person's own value, else Claude's inferred one. */
export function emergencyTarget(profile: Row[]): number | null {
  const rows = profile.filter((r) => str(r.key) === 'emergency_fund_target_months')
  const pick = rows.find((r) => !isInferredRow(r)) ?? rows[0]
  const n = parseFloat(str(pick?.value))
  return Number.isFinite(n) && n > 0 ? n : null
}
